import {
  ButtonStateColor,
  ButtonUses,
  SizeTokens,
  VariantTokens,
} from './Button.types';
import { CSSObject, useTheme } from 'foundations-theming/utils';
import { fwcBorderRadius } from 'foundations-theming/utils/fwc';
import type { Color, Theme } from 'foundations-theming/types';

import { BREAKPOINTS } from '../Grid/Grid.types';
import { rgba } from '../../utils/color';
import { useIconSize } from 'foundations-assets/icons/useIconSize';

const gtSmallMediaQuery = `@media only screen and (min-width: ${BREAKPOINTS.sm}px)`;
const smallMediaQuery = `@media only screen and (max-width: ${BREAKPOINTS.sm}px)`;

import { TruncateStringContainer } from '../TruncateString/TruncateString';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { isFormUse } from './Button.helpers';

import { StyledButton, UnstyledButton } from './PrivateButton';
import { StyledButtonLink, UnstyledButtonLink } from './PrivateButtonLink';

import {
  createActiveStateSelector,
  createHoverStateSelector,
} from '../../utils/styles/styles';

// The type for background and border colors was broadened in
// `ButtonStateStyles` to `Color | RGBAColorToken | (string & {})` to accommodate
// setting a rgba and transparent, non-token values for the ghost and transparent
// button variants. In the future, this value may come from a token
// and we can remove the variables and type casts below.
const toColor = (theme: Theme, buttonStateColor: ButtonStateColor) => {
  if (typeof buttonStateColor === 'object') {
    return rgba(theme.color[buttonStateColor.color], buttonStateColor.alpha);
  }

  return theme.color[buttonStateColor as Color] || buttonStateColor;
};

export const useUseVariantCssObject = (
  isLinkUse: boolean,
  variantTokens: VariantTokens
): CSSObject => {
  const theme = useTheme();

  return {
    display: 'inline-block',
    overflow: isLinkUse ? 'visible' : 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: isLinkUse ? 'inherit' : 'nowrap',
    backgroundColor: toColor(theme, variantTokens.idle.backgroundColor),
    borderColor: toColor(theme, variantTokens.idle.borderColor),
    color: toColor(theme, variantTokens.idle.color),
    textDecoration: variantTokens.idle.textDecoration,

    ...(isLinkUse
      ? { border: 0, verticalAlign: 'inherit' }
      : {
          borderRadius: theme.borderRadius[fwcBorderRadius('fwc-button')],
          borderWidth: theme.borderWidth['100'],
          borderStyle: 'solid',
          verticalAlign: 'middle',
        }),

    // hover styles should not override aria-invalid styles in an OPEN Select when anchorType='button'
    [createHoverStateSelector(
      ':hover:not([aria-disabled="true"],[aria-pressed="true"],[aria-invalid="true"])'
    )]: {
      backgroundColor: toColor(theme, variantTokens.hover.backgroundColor),
      borderColor: toColor(theme, variantTokens.hover.borderColor),
      color: toColor(theme, variantTokens.hover.color),
      textDecoration: variantTokens.hover.textDecoration,

      //stop changing the color of close icons.
      //happens in multi select, anchor type button, tags can have "white" close Xs for new badge style
      '& [data-action="close"] svg[data-icon-name]': {
        fill: 'currentcolor',
      },

      '& svg[data-icon-name]': {
        fill: toColor(theme, variantTokens.hover.color),
        transition: 'all 0.1s ease-in',
      },
    },

    [createActiveStateSelector(':active:not([aria-disabled="true"])')]: {
      backgroundColor: toColor(theme, variantTokens.active.backgroundColor),
      borderColor: toColor(theme, variantTokens.active.borderColor),
      color: toColor(theme, variantTokens.active.color),
      textDecoration: variantTokens.active.textDecoration,

      '& svg[data-icon-name]': {
        fill: toColor(theme, variantTokens.active.color),
      },
    },

    '&[aria-disabled="true"]': {
      backgroundColor: toColor(theme, variantTokens.disabled.backgroundColor),
      borderColor: toColor(theme, variantTokens.disabled.borderColor),
      color: toColor(theme, variantTokens.disabled.color),
      cursor: 'not-allowed',
      [`& ${TruncateStringContainer}`]: {
        cursor: 'not-allowed',
      },

      '& svg[data-icon-name]': {
        fill: toColor(theme, variantTokens.disabled.color),
      },

      // Override global a:hover styles for disabled buttons to prevent underlines
      '&:hover': {
        textDecoration: variantTokens.disabled.textDecoration || 'none',
      },
    },
  };
};

export const useSizeCssObject = (sizeTokens: SizeTokens): CSSObject => {
  const theme = useTheme();
  const iconSize = useIconSize(sizeTokens.iconSize)!;

  return {
    paddingBlockStart:
      sizeTokens.paddingBlockStart && theme.space[sizeTokens.paddingBlockStart],
    paddingBlockEnd:
      sizeTokens.paddingBlockEnd && theme.space[sizeTokens.paddingBlockEnd],
    paddingInlineStart: theme.space[sizeTokens.paddingInlineStart],
    paddingInlineEnd: theme.space[sizeTokens.paddingInlineEnd],
    width: sizeTokens.width,
    maxWidth: '100%',

    /**
     * We want the all icons including external link icons to be same height.
     *
     * Because ExternalLinkIcons in Buttons already have a span around them,
     * we can set the height of ExternalLinkIcons to the same value as the width.
     * The width and height of ExternalLinkIcons are set in PrivateButtonLink.
     *
     * For all other icons, we have to set the height to the same height it would
     * be if it was also wrapped in a span.
     *
     * Don't include ExternalLinkIcon because it's styled by Button and Link components.
     */
    '& svg[data-icon-name]': {
      width: iconSize,
      blockSize:
        theme.typography[sizeTokens.typography]?.lineHeight || iconSize,
    },

    '& svg[data-icon-name="ExternalLink"]': {
      width: 'auto',
      blockSize: 'auto',
    },
  };
};

/**
 * Note: a Button can be a target/anchor for a floating-ui element such as Popover, Dropdown etc.
 * However, floating-ui will add spans around the button to act as a focus guard for
 * accessibility. This causes our sibling selectors for autospacing adjacent sibling buttons
 * to break (as they are literally not siblings any longer and have spans injected between them)
 * so we need this hack selector to account for the floating-ui-focus-guard spans and target the
 * styled button after those too.
 * If modal={false} is set on a FloatingFocusManager, then 4 focus spans are injected.
 * if modal={true} only one of these spans is injected, so this selector aims to address both cases.
 */
const createAdjacentSiblingSelector = (use: ButtonUses): string => {
  const buttonComponent = use === 'unstyled' ? UnstyledButton : StyledButton;
  const buttonLinkComponent =
    use === 'unstyled' ? UnstyledButtonLink : StyledButtonLink;

  const selectors = [
    `& + ${buttonComponent}`,
    `& + ${buttonLinkComponent}`,
    `& + [data-floating-ui-focus-guard] + ${buttonComponent}`,
    `& + [data-floating-ui-focus-guard] + ${buttonLinkComponent}`,
    `& + [data-floating-ui-focus-guard] ~ [data-floating-ui-focus-guard] + ${buttonComponent}`,
    `& + [data-floating-ui-focus-guard] ~ [data-floating-ui-focus-guard] + ${buttonLinkComponent}`,
  ];

  return selectors.join(', ');
};

const getAutospacingStyles = (
  theme: Theme,
  responsive: boolean,
  use: ButtonUses
) => {
  const adjacentSiblingSelector = createAdjacentSiblingSelector(use);

  return {
    [adjacentSiblingSelector]: responsive
      ? {
          [gtSmallMediaQuery]: {
            marginInlineStart: theme.space['300'],
          },
          [smallMediaQuery]: {
            marginBlockStart: theme.space['200'],
          },
        }
      : {
          marginInlineStart: theme.space['300'],
        },
  };
};

export const useResponsiveStyles = ({
  responsive,
  use,
  isFilterBar,
}: {
  responsive: boolean;
  isFilterBar?: boolean;
  use: ButtonUses;
}): CSSObject => {
  const theme = useTheme();

  return {
    ...(responsive && {
      [smallMediaQuery]: {
        width: '100%',
      },
    }),
    ...(!isFilterBar && getAutospacingStyles(theme, responsive, use)),
  };
};

export const useDisplayOptimizedCssStyles = (use: ButtonUses): CSSObject => {
  const theme = useTheme();
  const isDisplayOptimized = useIsDisplayOptimized();
  return isDisplayOptimized && isFormUse(use)
    ? {
        backgroundColor: theme.color['fill-transparent-default'],
        border: 'none',
        paddingInlineStart: '0',
        paddingBlockStart: theme.space['100'],
        paddingBlockEnd: theme.space['50'],
        fontSize: theme.typography['body-100'].fontSize,
        '&:hover:not([aria-disabled="true"]), &:focus:not([aria-disabled="true"]), &:focus-visible:not([aria-disabled="true"]), &:active:not([aria-disabled="true"]), &[aria-disabled="true"]':
          {
            backgroundColor: theme.color['fill-transparent-default'],
          },
      }
    : {};
};
